import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import type {Dispatch, SetStateAction} from 'react';
import {useSelector} from 'react-redux';

import {ProcessFlowchartEditorConfigurator} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorConfigurator';
import {
  FlowchartEditorMode,
  isExecutionEditorMode,
  isExecutionViewerMode,
} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorModeConstants';
import {BenchlingState} from 'redux-stores/reduceApp';
import {getStore} from 'redux-stores/Store';
import {assertNever} from 'util/Types';

import {refreshNodeDisplayNames} from '../processFlowchartConfigEditorUtils';
import {FlowchartConfigWithMetadata} from '../redux/flowchartConfigAutosaveManagerCallbacks';
import type {Transition} from '../types/transition/TransitionTypes';
import {
  createInitialExecutionDraftState,
  mergeStatusCountsIntoExecutionDraft,
} from './createInitialExecutionDraftState';
import {useExecutionFlowchartCore} from './executionFlowchartCore';
import type {LineageDataResult, LoadedFlowchartData} from './executionFlowchartData';
import {LineageDataResultType, useProcessData, useTaskGroupLineageData} from './executionFlowchartData';
import type {ExecutionFlowchartReadinessSnapshot} from './ExecutionFlowchartPaneTypes';
import {
  getExecutionFlowchartIdFromProcess,
  getFlowchartConfigIdFromProcess,
  getFlowchartConfigVersionIdFromProcess,
} from './executionFlowchartProcessIds';

const EMPTY_LINEAGE_NODE_TASK_SCHEMA_IDS: Array<string> = [];

/**
 * EXECUTION_VIEWER builds transitions from template control-flow edges only (via
 * setFallbackTransitionSources), omitting optional schema-mapper transitions. When the user opens
 * the ACM editor, refresh from loaded data if the draft is that incomplete viewer bootstrap — but
 * preserve the draft when it already includes every loaded transition (re-entering after save).
 */
function shouldRefreshTransitionsFromLoadedData(
  draftTransitions: Record<string, Transition>,
  loadedTransitions: Record<string, Transition>
): boolean {
  const draftKeys = Object.keys(draftTransitions);
  const loadedKeys = Object.keys(loadedTransitions);
  if (loadedKeys.length === 0) {
    return false;
  }
  if (draftKeys.length === 0) {
    return true;
  }
  const allDraftKeysInLoaded = draftKeys.every((key) => Object.hasOwn(loadedTransitions, key));
  const loadedHasExtraKeys = loadedKeys.some((key) => !Object.hasOwn(draftTransitions, key));
  return allDraftKeysInLoaded && loadedHasExtraKeys;
}

/** Fills or updates the execution draft once template data, configurator, and lineage are ready. */
function useBootstrapExecutionDraft(args: {
  data: LoadedFlowchartData | null;
  executionEditorConfiguratorBase: ProcessFlowchartEditorConfigurator | null;
  setDraftState: Dispatch<SetStateAction<FlowchartConfigWithMetadata | null>>;
  lineageResult: LineageDataResult;
  flowchartConfigId: string | null;
  isSaveInFlight: boolean;
  onStaleDetected: () => void;
  shouldReseedDraft: boolean;
  onDraftReseeded: () => void;
}): void {
  const {
    data,
    executionEditorConfiguratorBase,
    setDraftState,
    lineageResult,
    flowchartConfigId,
    isSaveInFlight,
    onStaleDetected,
    shouldReseedDraft,
    onDraftReseeded,
  } = args;

  const lineageResultType = lineageResult.resultType;
  const lineageStatusCounts =
    lineageResultType === LineageDataResultType.SUCCESS ? lineageResult.data : undefined;

  useEffect(() => {
    if (data == null || executionEditorConfiguratorBase == null) {
      return;
    }
    if (lineageResultType === LineageDataResultType.PENDING) {
      return;
    }
    if (isSaveInFlight) {
      return;
    }

    // If the Apollo cache version doesn't match the Redux version (save completed but we haven't
    // refetched yet), trigger a networkOnly refetch and wait for fresh data.
    if (flowchartConfigId != null && data.flowchartConfigVersionId != null) {
      const reduxVersionId =
        getStore().getState().models.flowchartConfigs[flowchartConfigId]?.flowchartConfigMetadata
          ?.currentVersionId;
      if (reduxVersionId != null && reduxVersionId !== data.flowchartConfigVersionId) {
        onStaleDetected();
        return;
      }
    }

    if (shouldReseedDraft) {
      setDraftState((prev) => {
        if (prev != null) {
          // EXECUTION_VIEWER omits schema-mapper transitions; refresh from ACM-loaded data when the
          // draft is empty or only has viewer fallback transitions. Preserve when the draft already
          // includes every loaded transition (re-entering edit after a prior save).
          if (shouldRefreshTransitionsFromLoadedData(prev.metadata.transitions, data.initialTransitions)) {
            return {
              ...prev,
              metadata: {
                ...prev.metadata,
                transitions: data.initialTransitions,
              },
            };
          }
          return prev;
        }
        return createInitialExecutionDraftState(
          data,
          executionEditorConfiguratorBase.flowchartTypeName,
          lineageStatusCounts
        );
      });
      onDraftReseeded();
      return;
    }

    setDraftState((prev) => {
      if (prev == null) {
        return createInitialExecutionDraftState(
          data,
          executionEditorConfiguratorBase.flowchartTypeName,
          lineageStatusCounts
        );
      }
      if (lineageStatusCounts != null && Object.keys(lineageStatusCounts).length > 0) {
        const merged = mergeStatusCountsIntoExecutionDraft(prev, lineageStatusCounts);
        return merged === prev ? prev : merged;
      }
      return prev;
    });
  }, [
    data,
    executionEditorConfiguratorBase,
    lineageResultType,
    lineageStatusCounts,
    setDraftState,
    flowchartConfigId,
    isSaveInFlight,
    onStaleDetected,
    shouldReseedDraft,
    onDraftReseeded,
  ]);
}

interface UseExecutionFlowchartDraftStateResult {
  draftState: FlowchartConfigWithMetadata | null;
  setDraftState: Dispatch<SetStateAction<FlowchartConfigWithMetadata | null>>;
  clearDraft: () => void;
  readinessSnapshot: ExecutionFlowchartReadinessSnapshot;
  processIdsByNodeConfigId?: Record<string, string> | null;
  nodeUrlsByNodeConfigId?: Record<string, string> | null;
}

type UseExecutionFlowchartDraftStateParams =
  | {
      resolution: 'flowchartConfig';
      flowchartConfigId: string | null;
      editorMode: FlowchartEditorMode;
    }
  | {
      resolution: 'process';
      processId: string | null;
      editorMode: FlowchartEditorMode;
      lineageRootTaskId?: string;
    };

interface DraftResolutionDerived {
  flowchartConfigId: string | null;
  flowchartConfigVersionId: string | null;
  executionFlowchartId: string | null;
  externalInaccessible: boolean;
  externalPending: boolean;
}

/** Derives draft state from flowchart config or process, with external access checks. */
function useExecutionFlowchartDraftState(
  params: UseExecutionFlowchartDraftStateParams
): UseExecutionFlowchartDraftStateResult {
  const processIdForQuery = params.resolution === 'process' ? params.processId : null;
  const processData = useProcessData(processIdForQuery);

  const derived = useMemo((): DraftResolutionDerived => {
    switch (params.resolution) {
      case 'flowchartConfig':
        return {
          flowchartConfigId: params.flowchartConfigId,
          flowchartConfigVersionId: null,
          executionFlowchartId: null,
          externalInaccessible: false,
          externalPending: false,
        };
      case 'process':
        return {
          flowchartConfigId: getFlowchartConfigIdFromProcess(processData),
          flowchartConfigVersionId: getFlowchartConfigVersionIdFromProcess(processData),
          executionFlowchartId: getExecutionFlowchartIdFromProcess(processData),
          externalInaccessible:
            processData !== undefined &&
            (processData == null || getFlowchartConfigIdFromProcess(processData) == null),
          externalPending: processData === undefined,
        };
      default:
        return assertNever(params);
    }
  }, [params, processData]);

  const [draftState, setDraftState] = useState<FlowchartConfigWithMetadata | null>(null);
  const [forceNetworkOnly, setForceNetworkOnly] = useState(false);
  const [shouldReseedDraft, setShouldReseedDraft] = useState(false);

  const isSaveInFlight = useSelector((state: BenchlingState) =>
    derived.flowchartConfigId != null
      ? (state.models.flowchartConfigs[derived.flowchartConfigId]?.platformLiveProperties.isSaveInFlight ??
        false)
      : false
  );

  const skipFlowchartTypeConfigurator =
    (params.resolution === 'flowchartConfig' && params.flowchartConfigId == null) ||
    (params.resolution === 'process' && params.processId == null);
  const {isConfiguratorLoadError, executionEditorConfigurator, dataResult, data} = useExecutionFlowchartCore({
    flowchartConfigId: derived.flowchartConfigId,
    flowchartConfigVersionId: derived.flowchartConfigVersionId,
    executionFlowchartId: derived.executionFlowchartId,
    editorMode: params.editorMode,
    skipFlowchartTypeConfigurator,
    networkOnly: forceNetworkOnly,
  });

  const lineageNodeTaskSchemaIds = useMemo((): Array<string> => {
    const schemas = data?.availableTaskSchemas;
    if (schemas == null || schemas.length === 0) {
      return EMPTY_LINEAGE_NODE_TASK_SCHEMA_IDS;
    }
    return schemas.map((schema) => schema.id);
  }, [data]);

  const lineageProcessId = params.resolution === 'process' ? params.processId : null;
  const lineageRootTaskId = params.resolution === 'process' ? params.lineageRootTaskId : undefined;
  const lineageResult = useTaskGroupLineageData({
    flowchartId: derived.executionFlowchartId,
    flowchartProcessId: lineageProcessId,
    nodeTaskSchemaIds: lineageNodeTaskSchemaIds,
    lineageRootTaskId,
  });

  const clearDraft = useCallback((): void => {
    setDraftState(null);
  }, []);

  const handleStaleDetected = useCallback((): void => {
    setForceNetworkOnly(true);
  }, []);

  const prevEditorModeRef = useRef(params.editorMode);
  useEffect(() => {
    const previousEditorMode = prevEditorModeRef.current;
    if (previousEditorMode !== params.editorMode) {
      prevEditorModeRef.current = params.editorMode;
      if (isExecutionViewerMode(previousEditorMode) && isExecutionEditorMode(params.editorMode)) {
        setShouldReseedDraft(true);
      }
      setDraftState((prev) => {
        if (prev == null) {
          return prev;
        }
        const updatedNodes = refreshNodeDisplayNames(prev.staticFlowchart.nodesStaticData, params.editorMode);
        if (updatedNodes === prev.staticFlowchart.nodesStaticData) {
          return prev;
        }
        return {
          ...prev,
          staticFlowchart: {...prev.staticFlowchart, nodesStaticData: updatedNodes},
        };
      });
    }
  }, [params.editorMode]);

  const handleDraftReseeded = useCallback((): void => {
    setShouldReseedDraft(false);
  }, []);

  const setDraftStateAndClearNetworkOnly = useCallback(
    (updater: Parameters<typeof setDraftState>[0]) => {
      setDraftState(updater);
      // Once fresh data has been bootstrapped, clear the network-only flag so subsequent
      // queries can use the cache again.
      setForceNetworkOnly(false);
    },
    [setDraftState]
  );

  useBootstrapExecutionDraft({
    data,
    executionEditorConfiguratorBase: executionEditorConfigurator,
    setDraftState: setDraftStateAndClearNetworkOnly,
    lineageResult,
    flowchartConfigId: derived.flowchartConfigId,
    isSaveInFlight,
    onStaleDetected: handleStaleDetected,
    shouldReseedDraft,
    onDraftReseeded: handleDraftReseeded,
  });

  const readinessSnapshot: ExecutionFlowchartReadinessSnapshot = useMemo(
    () => ({
      isConfiguratorLoadError,
      dataResult,
      data,
      executionEditorConfigurator,
      externalInaccessible: derived.externalInaccessible,
      externalPending: derived.externalPending,
    }),
    [
      isConfiguratorLoadError,
      dataResult,
      data,
      executionEditorConfigurator,
      derived.externalInaccessible,
      derived.externalPending,
    ]
  );

  const processIdsByNodeConfigId =
    lineageResult.resultType === LineageDataResultType.SUCCESS
      ? lineageResult.processIdsByNodeConfigId
      : null;

  const nodeUrlsByNodeConfigId =
    lineageResult.resultType === LineageDataResultType.SUCCESS ? lineageResult.nodeUrlsByNodeConfigId : null;

  return {
    draftState,
    setDraftState,
    clearDraft,
    readinessSnapshot,
    processIdsByNodeConfigId,
    nodeUrlsByNodeConfigId,
  };
}

/** Draft + snapshot when the root entity is a flowchart config (ACM or viewer-by-config). */
export function useExecutionFlowchartDraftStateForConfig(args: {
  flowchartConfigId: string | null;
  editorMode: FlowchartEditorMode;
}): UseExecutionFlowchartDraftStateResult {
  return useExecutionFlowchartDraftState({
    resolution: 'flowchartConfig',
    flowchartConfigId: args.flowchartConfigId,
    editorMode: args.editorMode,
  });
}

/** Draft + snapshot when the root entity is a process (PCM, viewer-by-process, task group viewer). */
export function useExecutionFlowchartDraftStateForProcess(args: {
  processId: string;
  editorMode: FlowchartEditorMode;
  lineageRootTaskId?: string;
  skip?: boolean;
}): UseExecutionFlowchartDraftStateResult {
  return useExecutionFlowchartDraftState({
    resolution: 'process',
    processId: args.skip ? null : args.processId,
    editorMode: args.editorMode,
    lineageRootTaskId: args.lineageRootTaskId,
  });
}
